import React, { useState, useEffect } from 'react'
import DataKeys from 'k8s/DataKeys'
import InferActionParams from 'core/actions/InferActionParams'
import { listSecrets } from './actions'
import { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { pick } from 'ramda'
import useGlobalParams from 'core/hooks/useGlobalParams'
import { ArrayElement } from 'core/actions/Action'
import { secretsSelector } from './selectors'
import useListAction from 'core/hooks/useListAction'
import { useSelector } from 'react-redux'
import { routes } from 'core/utils/routes'
import DocumentMeta from 'core/components/DocumentMeta'
import ListContainer from 'core/containers/ListContainer'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import ClusterNameCell from 'k8s/components/common/cells/ClusterNameCell'
import { durationBetweenDates } from 'utils/misc'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import { ISecretsDetailsPageTabs } from './model'
import AddSecretModal from './AddSecretModal'
import { GridRowMenuItemSpec } from 'core/elements/grid/hooks/useGridRowMenu'
import DeleteSecretDialog from './DeleteSecretDialog'
import usePluginRouter from 'core/hooks/usePluginRouter'

type ModelDataKey = DataKeys.Secrets
type SelectorModel = ArrayElement<ReturnType<typeof secretsSelector>>
type ActionParams = InferActionParams<typeof listSecrets>
type Params = ActionParams & {
  masterNodeClusters: boolean
  healthyClusters: boolean
}

const requiredParams: Array<keyof ActionParams> = ['clusterId', 'namespace']
const defaultParams: Params & SortingState = {
  clusterId: null,
  namespace: null,
  masterNodeClusters: true,
  healthyClusters: true,
  orderBy: 'name',
  orderDirection: 'asc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>('Secrets', listTablePrefs)

const searchTargets = ['name', 'clusterName']

export const getSecretColumns = (pluginId = 'kubernetes'): GridViewColumn<SelectorModel>[] => {
  const routePath = pluginId === 'kubevirt' ? 'kubevirtConfiguration' : 'configuration'
  return [
    {
      key: 'name',
      label: 'Name',
      width: 'medium',
      CellComponent: createGridLinkCell({
        routeToFn: ({ clusterId, id }) =>
          routes[routePath].secrets.details.path({
            clusterId,
            id,
            tab: ISecretsDetailsPageTabs.Overview,
          }),
      }),
    },
    { key: 'clusterName', label: 'Cluster', width: 'medium', CellComponent: ClusterNameCell },
    { key: 'namespace', label: 'Namespace' },
    { key: 'type', label: 'Type' },
    {
      key: 'created',
      label: 'Age',
      formatFn: (value) => durationBetweenDates({ labels: ['d'] })(value),
    },
    { key: 'dataSize', label: 'Size' },
    {
      key: 'labels',
      label: 'Labels',
      disableSorting: true,
      CellComponent: createResourceLabelsCell({ type: 'table', separator: '=' }),
    },
    {
      key: 'annotations',
      label: 'Annotations',
      disableSorting: true,
      CellComponent: createResourceLabelsCell({ type: 'table', separator: ': ' }),
    },
  ]
}

export default function SecretsListPage() {
  const { currentPluginId } = usePluginRouter()
  const routePath = currentPluginId === 'kubevirt' ? 'kubevirtConfiguration' : 'configuration'
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { message, loading, reload } = useListAction(listSecrets, { params, requiredParams })
  const secrets = useSelector(secretsSelector)

  const columns = getSecretColumns(currentPluginId)

  const [selectedSecret, setSelectedSecret] = useState<SelectorModel>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false)

  const rowMenuItems: Array<GridRowMenuItemSpec<SelectorModel>> = [
    {
      label: 'Delete',
      icon: 'trash-alt',
      handleClick: (secret) => {
        setSelectedSecret(secret)
        setShowDeleteDialog(true)
      },
      refreshAfterSuccess: true,
    },
  ]

  return (
    <>
      <DocumentMeta title="Secrets" />
      <AddSecretModal addRoute={routes[routePath].secrets.add} />
      {showDeleteDialog && (
        <DeleteSecretDialog onClose={() => setShowDeleteDialog(false)} rows={[selectedSecret]} />
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.Secrets}
        searchTargets={searchTargets}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={message}
        onRefresh={reload}
        data={secrets}
        columns={columns}
        addUrl={routes[routePath].secrets.add.path()}
        addText="Add Secret"
        getParamsUpdater={getParamsUpdater}
        rowMenuItems={rowMenuItems}
        showItemsCountInLabel
        showRowMenuForSingleRowActions
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
